﻿<template>
	<div class="modal-confirm-customer-info modalInfo">
		<div
			class="modal-container modal-info"
			:style="{ zIndex: '9999999999' }"
		>
			<div class="modal-header">
				{{ title }}
			</div>
			<div class="modal-body">
				<div
					class="modal-confirm-customer-info-body-info"
					v-if="!displayQrCode && !successfullySent"
				>
					{{ info }}
				</div>
				<div
					class="modal-confirm-customer-info-body-info"
					v-if="displayQrCode"
					style="margin-bottom: 10px"
				>
					Please pick a device for the customer to complete their application and scan the QR code below
				</div>
				<div
					class="modal-confirm-customer-info-body-info"
					v-else-if="successfullySent"
					style="margin-bottom: 10px"
				>
					An email has been sent to the customer to complete their application.
				</div>
				<div style="display: grid; grid-template-columns: auto auto; justify-content: center; align-items: center; gap: 20px">
					<div v-if="!displayQrCode && !successfullySent">
						<div class="modal-confirm-customer-info-body-info">
							Full name:
							<span class="customer-info">{{ modal.passedData.customer.fullName }}</span>
						</div>
						<div class="modal-confirm-customer-info-body-info">
							Date of Birth:
							<span class="customer-info">{{ modal.passedData.customer.dob }}</span>
						</div>
						<div class="modal-confirm-customer-info-body-info">
							Email Address:
							<span class="customer-info">{{ modal.passedData.customer.email }}</span>
						</div>
						<div class="modal-confirm-customer-info-body-info">
							Phone Number:
							<span class="customer-info">{{ modal.passedData.customer.cell }}</span>
						</div>
					</div>

					<div>
						<span v-if="isLoadingQR"
							><i
								class="fas fa-circle-notch fa-spin"
								style="color: var(--main-color)"
							></i
						></span>
						<QrcodeSvg
							v-else-if="displayQrCode && !isLoadingQR"
							:class="{ 'qr-code': true, expanded: true }"
							:value="link"
							:size="180"
							level="H"
							renderAs="svg"
						/>
					</div>
				</div>
			</div>

			<div
				class="modal-footer"
				style="display: flex; flex-direction: row; align-items: center; justify-content: space-between"
			>
				<div style="display: flex; flex-direction: column; row-gap: 10px">
					<button
						v-if="(!displayQrCode && successfullySent) || (!displayQrCode && isLead)"
						class="fake-hyperlink click no-shadow"
						@click="toggleDisplayQRCode"
						:disabled="isLoading"
					>
						<span style="text-decoration: underline; font-size: small"> Customer Email Issues? </span>
					</button>
					<a
						v-show="!displayQrCode && showSMSHelp"
						href="#"
						class="button-link"
						style="font-size: small; color: var(--error-color)"
						@click="() => handleSMSIssue(modal.passedData.customer?.id, customerStoreCode, currentCustomerType)"
					>
						SMS Issues / Send Code To General Manager
					</a>
				</div>
				<div>
					<a
						v-if="displayQrCode && $global.isAdminView"
						class="button"
						:href="link"
						target="_blank"
						style="margin-right: 5px"
						>Admin Link</a
					>
					<button
						v-if="displayQrCode"
						@click="() => (displayQrCode = false)"
					>
						Back
					</button>
					<button
						v-if="displayQrCode || successfullySent"
						:class="cancelTextClass ? cancelTextClass : 'button-unwind'"
						@click="cancel()"
					>
						Close
					</button>
					<button
						v-if="cancelText && !displayQrCode && !successfullySent"
						:class="cancelTextClass ? cancelTextClass : 'button-unwind'"
						@click="cancel()"
					>
						{{ cancelText }}
					</button>
					<button
						v-if="acceptText && !displayQrCode && !successfullySent"
						:class="acceptTextClass ? acceptTextClass : 'button-save'"
						:disabled="displayQrCode || isLoading"
						@click="close()"
					>
						<i
							v-if="isLoading"
							class="fas fa-circle-notch fa-spin"
						/>
						<span v-else>{{ acceptText }}</span>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export interface IModalConfirmCustomerInfoProps {
		modal: {
			passedData?: {
				title?: 'Complete Quote';
				info?: 'Please confirm the e-mail and cellular phone number are accurate.  We will be sending an e-mail and SMS code to complete the quote application.';
				customer?: {
					fullName?: string;
					dob?: string;
					email?: string;
					cell?: string;
					id?: string;
					applicationId?: string;
					customerApplication?: {
						id?: string;
					};
					storeCodes?: string[];
				};
				acceptText?: string;
				acceptTextClass?: string;
				additionalData?: any;
				additionalInfo?: any;
				cancelText?: string;
				cancelTextClass?: string;
				isCustomer?: boolean;
				isLead?: boolean;
				language?: string;
				storeCode?: string;
				cancelFunction?: any;
			};
		};
	}
</script>
<script setup lang="ts">
	import { $globalProviderKey } from '@core/_globals/types/providerKeys';
	import { handleSMSIssue } from '@core/helpers/Customer/customer-helper';
	import api from '@core/services/api/_api';
	import $modal from '@core/services/modal';
	import util from '@core/services/util';
	import { QrcodeSvg } from 'qrcode.vue';
	import settings from 'settings';
	import { computed, inject, ref } from 'vue';

	import { CustomerType } from '@core/classes/SharedEnums';

	const { modal } = defineProps<IModalConfirmCustomerInfoProps>();
	const $global = inject($globalProviderKey);

	const acceptText = ref<string>(modal.passedData.acceptText);
	const acceptTextClass = ref(modal.passedData.acceptTextClass);
	const additionalData = ref(modal.passedData.additionalData);
	const additionalInfo = ref(modal.passedData.additionalInfo);
	const cancelText = ref<string>(modal.passedData.cancelText);
	const cancelTextClass = ref<string>(modal.passedData.cancelTextClass);
	const displayQrCode = ref(false);
	const info = ref(modal.passedData.info);
	const isCustomer = ref(modal.passedData.isCustomer ?? false);
	const isLead = ref(modal.passedData.isLead);
	const isLoading = ref(false);
	const isLoadingQR = ref(false);
	const link = ref<string>('');
	const storeCode = ref<string>(modal.passedData.storeCode);
	const successfullySent = ref<boolean>(false);
	const title = ref('Information');

	const showSMSHelp = computed(() => !!modal.passedData.customer?.id);

	const currentCustomerType = computed(() => (isLead.value ? CustomerType.Lead : CustomerType.Customer));
	const customerStoreCode = computed(() => storeCode.value ?? modal.passedData.customer?.storeCodes?.[0]);

	if (modal.passedData.title) title.value = modal.passedData.title;

	async function close() {
		let response = false;

		if (isCustomer.value) {
			response = await createCustomerApplication(modal.passedData.customer);
		} else {
			response = await sendApplication(modal.passedData.customer);
		}

		if (response) {
			successfullySent.value = true;
			//$modal.close();
		}
	}

	function cancel() {
		if (typeof modal.passedData.cancelFunction == 'function') {
			modal.passedData.cancelFunction();
		}
		$modal.cancel();
	}

	async function toggleDisplayQRCode() {
		isLoadingQR.value = true;
		// If it's create and not resend.
		try {
			const response = await api.applications.getCustomerApplicationLink({
				customerId: modal.passedData.customer.id,
				isInStore: true,
			});
			if (response.data) link.value = response.data;
			else throw new Error("Couldn't generate link");
		} catch (err) {
			util.toastr('error', 'Error', 'Could not generate QR code.');
			console.error(err);
		} finally {
			isLoadingQR.value = false;
			if (link.value) displayQrCode.value = true;
		}
	}

	async function sendApplication(customer: any) {
		isLoading.value = true;
		let success = false;

		try {
			const response = await api.applications.reSendApplicationLink(customer);
			if (response.data == true) {
				success = true;
				util.toastr('success', 'Success', 'Email sent to customer.');
			} else {
				util.toastr('error', 'Error', 'Email could not be sent to client.');
			}
		} catch (err) {
			console.error(err);
			util.toastr('error', 'Error', 'Email could not be sent to client.');
		} finally {
			isLoading.value = false;
		}

		return success;
	}

	async function createCustomerApplication(customer: any) {
		let success = false;
		isLoading.value = true;

		try {
			const response = await api.applications.createApplication({
				prefilledData: {
					language: modal.passedData.language,
					firstName: customer.firstName,
					lastName: customer.lastName,
					dateOfBirth: customer.dob,
					phoneNumber: customer.cell,
					emailAddress: customer.email,
					storeCode: storeCode.value,
				},
				customerId: customer.id,
			});

			if (response.data) {
				success = true;
				util.toastr('success', 'Success', 'Application Created.');
			}

			util.logRocketTrack(`Customer_App_Creation`, {
				customerId: customer.id,
				customerCell: customer.cell,
				customerEmail: customer.email,
				customerDob: customer.dob,
				storeCode: storeCode.value,
				environment: settings.environmentName,
			});
		} catch (err) {
			console.error(err);
			util.toastr('error', 'Error', 'Could not create application.');
		} finally {
			isLoading.value = false;
		}

		return success;
	}
</script>
<style>
	.modalInfo {
		margin: auto;
	}

	.modalInfo .modal-body {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	.modal-confirm-customer-info .modalInfo .modal-confirm-customer-info-body-info {
		white-space: pre-wrap;
		margin: 10px 0;
		text-align: start;
	}

	.modalInfo .modal-body-info-important {
		font-weight: bold;
	}

	.modalInfo .modal-container {
		width: auto;
		max-width: 815px;
	}

	.modal-footer-test {
	}
	.customer-info {
		font-weight: bold;
	}

	.click {
		cursor: pointer;
	}

	.fake-hyperlink {
		all: unset;
		cursor: pointer;
		color: var(--error-color);
	}
</style>
